/**
 * Trend charts for /admin/health.
 *
 * Reads the last 168h (default 7d) of hourly snapshots from
 * `/api/admin/health/history` and renders three small line/area charts:
 *   1. Closeout queue depth (pending / running / failed)
 *   2. Closeout PDF generation times (median / p95)
 *   3. Errors in the last 24h (failed jobs / locked users)
 *
 * Kept as a separate component so HealthPage stays small and the trend
 * UI can be reused (e.g. an exec summary page) without dragging in the
 * live status board.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import { TrendingUp, AlertCircle, Loader2 } from "lucide-react";

function fmtTick(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return `${String(d.getUTCMonth() + 1).padStart(2, "0")}/${String(d.getUTCDate()).padStart(2, "0")} ${String(d.getUTCHours()).padStart(2, "0")}h`;
  } catch {
    return iso;
  }
}

function TrendCard({ title, children, testid }) {
  return (
    <Card className="bg-slate-900 border-slate-800">
      <CardContent className="p-4" data-testid={testid}>
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-3.5 h-3.5 text-blue-400" />
          <div className="font-display font-bold tracking-tight">{title}</div>
        </div>
        <div className="h-44">{children}</div>
      </CardContent>
    </Card>
  );
}

const AXIS_PROPS = {
  stroke: "#475569",
  tick: { fill: "#94a3b8", fontSize: 10 },
  tickLine: false,
};

const TOOLTIP_STYLE = {
  contentStyle: {
    background: "#0f172a",
    border: "1px solid #334155",
    borderRadius: "6px",
    fontSize: "12px",
  },
  labelStyle: { color: "#cbd5e1" },
};

export default function HealthTrends({ hours = 168 }) {
  const [points, setPoints] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBusy(true);
      try {
        const { data } = await api.get(`/admin/health/history?hours=${hours}`);
        if (!cancelled) {
          setPoints(data.points || []);
          setErr(null);
        }
      } catch (e) {
        if (!cancelled) {
          setErr(e?.response?.data?.detail || e?.message || "Failed to load trends");
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hours]);

  const series = useMemo(() => (points || []).map((p) => ({
    ...p,
    label: fmtTick(p.taken_at),
  })), [points]);

  if (busy && !points) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-400 py-6" data-testid="health-trends-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading trends…
      </div>
    );
  }

  if (err) {
    return (
      <div className="rounded-md border border-red-900 bg-red-950/40 text-red-200 p-3 text-sm flex items-center gap-2" data-testid="health-trends-error">
        <AlertCircle className="w-4 h-4" />
        {err}
      </div>
    );
  }

  if (!series.length) {
    return (
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-4" data-testid="health-trends-empty">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-3.5 h-3.5 text-slate-500" />
            <div className="font-display font-bold tracking-tight">Trends</div>
          </div>
          <div className="text-xs text-slate-500 italic">
            No history yet — the scheduler captures one snapshot per hour. Check back in an hour.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-5" data-testid="health-trends">
      <TrendCard title="Queue depth (last 7d)" testid="trend-queue">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={series} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
            <XAxis dataKey="label" {...AXIS_PROPS} interval="preserveStartEnd" minTickGap={40} />
            <YAxis {...AXIS_PROPS} width={36} allowDecimals={false} />
            <Tooltip {...TOOLTIP_STYLE} />
            <Legend wrapperStyle={{ fontSize: "11px" }} />
            <Line type="monotone" dataKey="queue_pending" stroke="#60a5fa" strokeWidth={2} dot={false} name="Pending" />
            <Line type="monotone" dataKey="queue_running" stroke="#34d399" strokeWidth={2} dot={false} name="Running" />
            <Line type="monotone" dataKey="queue_failed" stroke="#f87171" strokeWidth={2} dot={false} name="Failed" />
          </LineChart>
        </ResponsiveContainer>
      </TrendCard>

      <TrendCard title="Closeout times (seconds)" testid="trend-durations">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={series} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
            <XAxis dataKey="label" {...AXIS_PROPS} interval="preserveStartEnd" minTickGap={40} />
            <YAxis {...AXIS_PROPS} width={36} />
            <Tooltip {...TOOLTIP_STYLE} />
            <Legend wrapperStyle={{ fontSize: "11px" }} />
            <Line type="monotone" dataKey="closeout_median_seconds" stroke="#60a5fa" strokeWidth={2} dot={false} name="Median" connectNulls />
            <Line type="monotone" dataKey="closeout_p95_seconds" stroke="#fbbf24" strokeWidth={2} dot={false} name="p95" connectNulls />
          </LineChart>
        </ResponsiveContainer>
      </TrendCard>

      <TrendCard title="Errors (last 24h rolling)" testid="trend-errors">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={series} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
            <XAxis dataKey="label" {...AXIS_PROPS} interval="preserveStartEnd" minTickGap={40} />
            <YAxis {...AXIS_PROPS} width={36} allowDecimals={false} />
            <Tooltip {...TOOLTIP_STYLE} />
            <Legend wrapperStyle={{ fontSize: "11px" }} />
            <Line type="monotone" dataKey="failed_jobs_24h" stroke="#f87171" strokeWidth={2} dot={false} name="Failed jobs" />
            <Line type="monotone" dataKey="login_failed_24h" stroke="#fb923c" strokeWidth={2} dot={false} name="Login fails" />
            <Line type="monotone" dataKey="locked_users" stroke="#a78bfa" strokeWidth={2} dot={false} name="Locked users" />
          </LineChart>
        </ResponsiveContainer>
      </TrendCard>
    </div>
  );
}
